import { IconToggleButton } from '@clay-run/glaze/toggle-button';
import { FLOW_ICONS } from '../icons';
import { HeadsetButton } from './HeadsetButton';
import { togglePanel, toggleFlowClosed, useFlowPanelsStore } from './panels/flow-panels-store';

/**
 * The three mode toggles — Chat · Discover · Actions, left-to-right in the
 * same order the columns paint (R8) — driving the V2 three-column panel container
 * (`panels/FlowPanels.tsx`) through its session-only store
 * (`panels/flow-panels-store.ts`). Discover's toggle reads `!flowClosed`
 * (pressed == the page is visible, like Chat/Actions highlighting their
 * surface; unpressed == the Discover view has replaced the page, app-wide), and R4 holds: the Chat/Actions
 * toggles never touch `flowClosed`. The Actions toggle only flips
 * the panel store; `useActionsDockSync` keeps the enroll-queue dock in
 * lockstep with it.
 *
 * ── Who renders this, and where (Spencer QA rounds 2–3) ──────────────────
 * Exactly ONE component, mounted in exactly one VISIBLE place per state, and
 * always at the TOP-RIGHT CORNER OF THE WHOLE SHELL — the right edge of
 * whichever column paints rightmost. `derivePanelTogglesHost`
 * (`panels/flow-panels-layout.ts`, read via `panels/usePanelTogglesHost.ts`)
 * names the host; each host renders this only when named:
 *  - `rail/RailDock.tsx` — right edge of the Actions strip, whenever docked
 *    in push mode (the Actions column is a flex sibling at the row's right
 *    edge). Round 2 did this only while Flow was collapsed (Chat + Actions
 *    open, `flowClosed`); round 3 generalised it because with Actions open
 *    and Flow visible the toggles sat at the MIDDLE column's edge, stranded
 *    mid-screen. Collapsed is still covered — it implies Actions open.
 *  - `Header.tsx` — last child of the Flow column's header otherwise:
 *    Actions closed (Flow is rightmost), or overlay mode (an open Actions
 *    panel floats UNDER the full-width Flow header, R10). The search takes
 *    the toggles' place at the header's right edge when they leave.
 * There is deliberately no third, standalone toggle row — round 1's
 * `<Header compact/>` strip stacked a second row above the panel row and is
 * gone. Reads the store itself so neither host needs to thread props; the
 * two gates are mutually exclusive by construction, so a tree holds at most
 * one copy (`Header.test.tsx` / `RailDock.test.tsx` each mount one host and
 * still scope with `within` out of habit).
 *
 * R7: Glaze's `ghost` variant paints pressed as a text COLOUR only
 * (`data-pressed:…:text-content-selected`; its only background rule is
 * `data-pressed:…:hover:bg-bg-selected`), so an unhovered pressed toggle reads
 * as plain blue text. `data-pressed:bg-bg-selected` adds the resting fill. It
 * is additive, not merged away: `IconToggleButton` is built with `tv()`
 * (tailwind-merge), and this class's variant chain (`data-pressed:` alone)
 * differs from the existing hover-only rule's (`data-pressed:…:hover:`), so
 * tailwind-merge keeps both. `IconToggleButton`'s `pressed` IS the selected
 * visual (`data-pressed:` variants) — Base UI's `Toggle` emits `aria-pressed`.
 *
 * The Trellus headset button trails the cluster (`HeadsetButton.tsx`): absent, not
 * dead, without the extension, the capability or Terra Beta.
 *
 * No role-based filtering: every role gets Chat and Discover, in whichever
 * host renders this, so the Discover toggle can always bring the page back and
 * the Chat toggle can always close a Chat panel opened from elsewhere. Actions
 * is subject to the app's suppression (`actionsSuppressed`) and is absent, not
 * dead, when an app hides the enroll queue.
 */
export function PanelToggles() {
  // Three selector subscriptions, not a whole-state subscription (D10) — the
  // host header must not re-render on a drag-share write.
  const chatOpen = useFlowPanelsStore((s) => s.chatOpen);
  const actionsOpen = useFlowPanelsStore((s) => s.actionsOpen);
  const flowClosed = useFlowPanelsStore((s) => s.flowClosed);
  // `shell.hideEnrollQueue` apps hold Actions closed; the toggle is absent, not dead.
  const actionsSuppressed = useFlowPanelsStore((s) => s.actionsSuppressed);

  return (
    <div data-testid="panel-toggles" className="flex shrink-0 items-center gap-1">
      <IconToggleButton
        size="sm"
        variant="ghost"
        className="shrink-0 data-pressed:bg-bg-selected"
        icon={FLOW_ICONS.chat}
        aria-label="Chat panel"
        pressed={chatOpen}
        onPressedChange={() => togglePanel('chat')}
      />
      <IconToggleButton
        size="sm"
        variant="ghost"
        className="shrink-0 data-pressed:bg-bg-selected"
        icon={FLOW_ICONS.search}
        aria-label="Discover"
        pressed={!flowClosed}
        onPressedChange={() => toggleFlowClosed()}
      />
      {!actionsSuppressed && (
        <IconToggleButton
          size="sm"
          variant="ghost"
          className="shrink-0 data-pressed:bg-bg-selected"
          icon={FLOW_ICONS.actions}
          aria-label="Actions panel"
          pressed={actionsOpen}
          onPressedChange={() => togglePanel('actions')}
        />
      )}
      {/* Trellus dialer sidebar toggle — renders nothing unless Trellus is present + advertises sidebar.toggle + Terra Beta. */}
      <HeadsetButton />
    </div>
  );
}
